import styled from '@emotion/styled';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { ChevronIcon } from 'src/domains/layouts/shared/assets/icons/ChevronIcon';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { css, SerializedStyles } from '@emotion/react';

const setEventRow = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
            padding: ${theme.star.padding.small};
            &:hover {
                background-color: ${theme.star.eventRow6PackColors.bgColor};
            }
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            border-radius: ${theme.star.radius.none};
            padding: ${theme.star.padding.medium};
            border: none;
            border-bottom: 1px solid ${theme.star.unified.eventRow.borderColor};
            gap: ${theme.star.gap.medium};

            &:hover {
                background-color: transparent;
            }

            & [class$='EventRowSelectionsContainer'] {
                gap: ${theme.star.gap.small};
            }

            & [class$='EventRowNameContainer'] {
                @media ${theme.star.mediaQuery.desktop} {
                    display: flex;
                    flex-flow: column;
                    align-items: flex-start;
                    gap: ${theme.star.gap.small};
                }
            }

            & [class$='EventRowAdditions'] {
                padding: 0;
            }

            & [class$='EventRowItem'] {
                height: 48px;
            }

            & [class$='EventRowName'] {
                padding: 0;
                gap: ${theme.star.gap.small};           
            }

            &::before {
                display: none;
            }
        `;
    }
    return css`
        border-left: 1px solid ${theme.star.eventRow.borderColor};
        border-right: 1px solid ${theme.star.eventRow.borderColor};
        &:hover {
            background-color: ${theme.star.eventRow6PackColors.bgColor};
        }
        &::before {
            content: '';
            position: absolute;
            border-top: 1px solid ${theme.star.eventRow.borderColor};
            width: 100%;
            top: -1px;
        }
    `;
};

interface EventRowPropsType {
    variant: DefaultComponentVariantType | null;
}

export const EventRow = withConfig(theme => styled('section', { label: 'EventRow' })<EventRowPropsType>`
    ${({ variant }): SerializedStyles => setEventRow(variant, theme)}
    background-color: ${theme.star.eventRow.bgColor};
    display: flex;
    flex-direction: column;
    transition: background-color .2s ease;
    position: relative;
    
    @media ${theme.star.mediaQuery.desktop} {
        flex-direction: row;
    }
`);

export const EventRowName = withConfig(theme => styled(Link, { label: 'EventRowName' })`
    color: inherit;
    display: flex;
    flex-flow: column nowrap;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.medium};
    gap: 8px;
    justify-content: center;
    line-height: 1;
    min-height: 80px;
    overflow: hidden;
    padding: 12px;
    position: relative;
    text-decoration: none;
    text-overflow: ellipsis;
    width: 100%;
    @media ${theme.star.mediaQuery.desktop} {
        justify-content: flex-start;
        gap: 0;
        min-height: 88px;
        padding: 8px 12px;
    }
`);

export const EventRowNameContainer = withConfig(theme => styled('div', { label: 'EventRowNameContainer' })`
    align-items: center;
    color: ${theme.star.eventRow.txtColor};
    display: flex;
    flex-flow: row nowrap;
    gap: 32px;
    @media ${theme.star.mediaQuery.desktop} {
        display: block;
    }
`);

export const EventRowAdditions = withConfig(theme => styled('div', { label: 'EventRowAdditions' })`
    align-items: center;
    align-self: center;
    display: flex;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.regular};
    justify-content: center;
    text-align: center;
    padding: 4px 8px;
    @media ${theme.star.mediaQuery.desktop} {
        align-self: start;
        gap: 8px;
        padding: 0 0 0 40px;
    }
`);

export const EventRowMoreMarketsLink = withConfig(theme => styled('span', { label: 'EventRowMoreMarketsLink' })`
    align-items: center;
    color: ${theme.star.eventRow6PackColors.txtColor};
    cursor: pointer;
    display: flex;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3334;
`);

export const MoreMarketChevron = styled(ChevronIcon, { label: 'MoreMarketChevron' })`
    width: 12px;
    margin: 0 0 0 4px;
    fill: currentcolor;
`;

export const EventRowMobileChevron = withConfig(theme => styled(ChevronIcon, { label: 'EventRowMobileChevron' })`
    fill: ${theme.star.eventRow6PackColors.bgColorSecondary};
    margin-top: -10px;
    position: absolute;
    right: 8px;
    top: 50%;
    width: 12px;
`);
interface EventRowSelectionsContainerPropsType {
    variant: DefaultComponentVariantType | null;
}
export const EventRowSelectionsContainer = withConfig(theme => styled('div', { label: 'EventRowSelectionsContainer' })<EventRowSelectionsContainerPropsType>`
    display: flex;
    width: 100%;
    gap: ${({ variant }): string =>
        variant === DefaultComponentVariantType.SECONDARY || variant === DefaultComponentVariantType.TERTIARY
            ? theme.star.gap.small
            : '0'};
    @media ${theme.star.mediaQuery.desktop} {
        flex: 0 0 444px;
        width: 444px;
    }
    @media ${theme.star.mediaQuery.xxLargeDesktop} {
        flex-basis: 480px;
        width: 480px;
    }
`);
